import type ApolloCache from '@wora/apollo-cache'
import type { ContactQuery, Contact, SearchContactsQuery } from '../generated/types'
import { SearchContactsDocument, ContactDocument, FullContactFragmentDoc } from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

/**
 * Updates any cached contacts in the apollo cache
 */
export function updateCachedContact(proxy: CacheProxy, contact: Contact) {
  const cache = proxy as ApolloCache
  cache.writeQuery({
    query: ContactDocument,
    variables: { id: contact._id },
    data: {
      contact: {
        __typename: 'Contact',
        ...contact
      }
    }
  })

  // update all searchContact queries to remove the contact
  if (contact._deleted) {
    updateQueries<SearchContactsQuery>(cache, {
      query: SearchContactsDocument,
      update: (data) => {
        const filteredContacts = data.searchContacts.contacts.filter((q) => q._id !== contact._id)

        return {
          ...data,
          searchContacts: {
            ...data.searchContacts,
            contacts: filteredContacts,
            total_rows:
              filteredContacts.length !== data.searchContacts.contacts.length
                ? data.searchContacts.total_rows - 1
                : data.searchContacts.total_rows
          }
        }
      }
    })
  }

  cache.writeFragment({
    fragment: FullContactFragmentDoc,
    id: defaultDataIdFromObject({ id: contact._id, __typename: 'Contact' }),
    data: {
      __typename: 'Contact',
      ...contact
    }
  })
}

export function updateCachedContactPartially(cache: ApolloCache, contactId: string, values: Partial<Contact>) {
  try {
    const contact = cache.readQuery<ContactQuery>({
      query: ContactDocument,
      variables: { id: contactId }
    }).contact

    updateCachedContact(cache, {
      ...contact,
      ...values
    })
  } catch {
    console.warn('Unable to find contact for id ' + contactId)
  }
}
